/**
 * Canvas state management hook.
 * See: specs/017-json-canvas/data-model.md
 *
 * Component-based model:
 * - Canvas = named container holding multiple components
 * - CanvasComponent = individual element with unique componentId within a canvas
 * - Updates target specific components by ID (upsert/remove)
 *
 * Manages:
 * - Multiple canvases (up to MAX_CANVASES)
 * - Multiple components per canvas (up to MAX_COMPONENTS_PER_CANVAS)
 * - Canvas/component lifecycle (streaming -> complete -> error)
 * - Session storage persistence
 * - Panel visibility and width
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type {
  Canvas,
  CanvasComponent,
  CanvasState,
  CanvasUpdateData,
  CanvasCompleteData,
  CanvasErrorData,
  ComponentTree,
} from '../types/canvas';
import {
  MAX_CANVASES,
  MAX_COMPONENTS_PER_CANVAS,
  CANVAS_ID_PATTERN,
} from '../types/canvas';

// ============================================================================
// Session Storage Keys
// ============================================================================

const STORAGE_KEY_PREFIX = 'canvas-state';

function getStorageKey(sessionId: string): string {
  return `${STORAGE_KEY_PREFIX}-${sessionId}`;
}

// ============================================================================
// Serialization Helpers
// ============================================================================

interface SerializedCanvasComponent {
  id: string;
  artifactId: string;
  jsonTree: ComponentTree;
  status: string;
  error?: string;
  updatedAt: number;
  createdAt: number;
}

interface SerializedCanvas {
  id: string;
  components: Array<[string, SerializedCanvasComponent]>;
  status: string;
  error?: string;
  updatedAt: number;
  createdAt: number;
}

interface SerializedCanvasState {
  canvases: Array<[string, SerializedCanvas]>;
  activeCanvasId: string | null;
  activeComponentId: string | null;
  panelVisible: boolean;
  panelWidth: number;
}

function serializeState(state: CanvasState): SerializedCanvasState {
  const serializedCanvases: Array<[string, SerializedCanvas]> = [];
  for (const [canvasId, canvas] of state.canvases) {
    const serializedComponents: Array<[string, SerializedCanvasComponent]> = [];
    for (const [compId, comp] of canvas.components) {
      serializedComponents.push([compId, {
        id: comp.id,
        artifactId: comp.artifactId,
        jsonTree: comp.jsonTree,
        status: comp.status,
        error: comp.error,
        updatedAt: comp.updatedAt,
        createdAt: comp.createdAt,
      }]);
    }
    serializedCanvases.push([canvasId, {
      id: canvas.id,
      components: serializedComponents,
      status: canvas.status,
      error: canvas.error,
      updatedAt: canvas.updatedAt,
      createdAt: canvas.createdAt,
    }]);
  }
  return {
    canvases: serializedCanvases,
    activeCanvasId: state.activeCanvasId,
    activeComponentId: state.activeComponentId,
    panelVisible: state.panelVisible,
    panelWidth: state.panelWidth,
  };
}

function deserializeState(data: SerializedCanvasState): CanvasState {
  const canvases = new Map<string, Canvas>();
  for (const [canvasId, serializedCanvas] of data.canvases) {
    const components = new Map<string, CanvasComponent>();
    for (const [compId, comp] of serializedCanvas.components) {
      components.set(compId, {
        id: comp.id,
        artifactId: comp.artifactId,
        jsonTree: comp.jsonTree,
        status: comp.status as CanvasComponent['status'],
        error: comp.error,
        updatedAt: comp.updatedAt,
        createdAt: comp.createdAt,
      });
    }
    canvases.set(canvasId, {
      id: serializedCanvas.id,
      components,
      status: serializedCanvas.status as Canvas['status'],
      error: serializedCanvas.error,
      updatedAt: serializedCanvas.updatedAt,
      createdAt: serializedCanvas.createdAt,
    });
  }
  return {
    canvases,
    activeCanvasId: data.activeCanvasId,
    activeComponentId: data.activeComponentId ?? null,
    panelVisible: data.panelVisible,
    panelWidth: data.panelWidth,
  };
}

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseCanvasReturn {
  // State
  canvases: Map<string, Canvas>;
  activeCanvasId: string | null;
  activeComponentId: string | null;
  activeCanvas: Canvas | null;
  panelVisible: boolean;
  panelWidth: number;
  canvasCount: number;
  componentCount: number; // Total components across all canvases
  isAtLimit: boolean;

  // Connection state (T068a)
  isConnectionLost: boolean;
  hasStreamingCanvases: boolean;

  // Event Handlers (called by useChat callbacks)
  handleUpdate: (data: CanvasUpdateData) => void;
  handleComplete: (data: CanvasCompleteData) => void;
  handleError: (data: CanvasErrorData) => void;
  handleConnectionLost: () => void;
  handleConnectionRestored: () => void;

  // Actions
  closeCanvas: (canvasId: string) => void;
  closeComponent: (canvasId: string, componentId: string) => void;
  setActiveCanvas: (canvasId: string) => void;
  setActiveComponent: (canvasId: string, componentId: string) => void;
  togglePanel: () => void;
  setPanelWidth: (width: number) => void;
  clearAllCanvases: () => void;
}

// ============================================================================
// Hook Options
// ============================================================================

export interface UseCanvasOptions {
  sessionId: string | null;
  autoShowOnContent?: boolean; // Default: true - auto-show panel when content arrives
  minPanelWidth?: number; // Default: 300
  maxPanelWidth?: number; // Default: 600
}

// ============================================================================
// useCanvas Hook
// ============================================================================

export function useCanvas(options: UseCanvasOptions): UseCanvasReturn {
  const {
    sessionId,
    autoShowOnContent = true,
    minPanelWidth = 300,
    maxPanelWidth = 600,
  } = options;

  // State
  const [canvases, setCanvases] = useState<Map<string, Canvas>>(new Map());
  const [activeCanvasId, setActiveCanvasId] = useState<string | null>(null);
  const [activeComponentId, setActiveComponentIdState] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelWidth, setPanelWidthState] = useState(400);
  const [isConnectionLost, setIsConnectionLost] = useState(false);

  // Track if we've loaded from storage for current session
  const loadedSessionRef = useRef<string | null>(null);

  // ============================================================================
  // Session Storage Persistence (T015)
  // ============================================================================

  // Load from session storage on mount or when sessionId changes
  useEffect(() => {
    if (!sessionId) return;

    // Skip if we've already loaded for this session
    if (loadedSessionRef.current === sessionId) return;

    // Reset state for new session before loading
    if (loadedSessionRef.current !== null && loadedSessionRef.current !== sessionId) {
      // Session changed - reset to clean state first
      setCanvases(new Map());
      setActiveCanvasId(null);
      setActiveComponentIdState(null);
      setPanelVisible(false);
      setPanelWidthState(400);
    }

    const storageKey = getStorageKey(sessionId);
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as SerializedCanvasState;
        const state = deserializeState(parsed);
        setCanvases(state.canvases);
        setActiveCanvasId(state.activeCanvasId);
        setActiveComponentIdState(state.activeComponentId);
        setPanelVisible(state.panelVisible);
        setPanelWidthState(state.panelWidth);
      }
      loadedSessionRef.current = sessionId;
    } catch (e) {
      console.warn('Failed to load canvas state from session storage:', e);
      loadedSessionRef.current = sessionId; // Mark as loaded even on error to prevent loops
    }
  }, [sessionId]);

  // Save to session storage on state changes
  useEffect(() => {
    if (!sessionId) return;

    const storageKey = getStorageKey(sessionId);
    try {
      const state: CanvasState = {
        canvases,
        activeCanvasId,
        activeComponentId,
        panelVisible,
        panelWidth,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(serializeState(state)));
    } catch (e) {
      console.warn('Failed to save canvas state to session storage:', e);
    }
  }, [sessionId, canvases, activeCanvasId, activeComponentId, panelVisible, panelWidth]);

  // ============================================================================
  // Canvas Limit Enforcement (T016)
  // ============================================================================

  const canvasCount = canvases.size;
  const componentCount = Array.from(canvases.values()).reduce(
    (total, canvas) => total + canvas.components.size,
    0
  );
  const isAtLimit = canvasCount >= MAX_CANVASES;

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleUpdate = useCallback(
    (data: CanvasUpdateData) => {
      console.log('[useCanvas] handleUpdate called with:', data);
      const { canvasId, componentId, artifactId, jsonTree, action = 'upsert', progress } = data;

      // Validate canvas ID format
      if (!CANVAS_ID_PATTERN.test(canvasId)) {
        console.error(`Invalid canvas ID format: ${canvasId}`);
        return;
      }

      // Use stable component ID: prefer provided componentId, then artifactId, then fallback
      const compId = componentId || artifactId || `component-${canvasId}`;

      setCanvases((prev) => {
        const now = Date.now();
        const updated = new Map(prev);
        let canvas = updated.get(canvasId);

        // Handle remove action
        if (action === 'remove') {
          if (canvas && canvas.components.has(compId)) {
            const newComponents = new Map(canvas.components);
            newComponents.delete(compId);

            // If canvas is empty after removal, optionally remove it too
            if (newComponents.size === 0) {
              updated.delete(canvasId);
            } else {
              updated.set(canvasId, {
                ...canvas,
                components: newComponents,
                updatedAt: now,
              });
            }
          }
          return updated;
        }

        // Handle upsert action
        if (!canvas) {
          // Check limit for new canvases
          if (prev.size >= MAX_CANVASES) {
            console.warn(
              `Canvas limit (${MAX_CANVASES}) reached. Cannot create canvas: ${canvasId}`
            );
            return prev;
          }

          // Create new canvas
          canvas = {
            id: canvasId,
            components: new Map(),
            status: 'streaming',
            updatedAt: now,
            createdAt: now,
          };
        }

        // Check component limit
        if (!canvas.components.has(compId) && canvas.components.size >= MAX_COMPONENTS_PER_CANVAS) {
          console.warn(
            `Component limit (${MAX_COMPONENTS_PER_CANVAS}) reached for canvas: ${canvasId}`
          );
          return prev;
        }

        // Create/update component - preserve existing jsonTree if new one is undefined
        const existingComponent = canvas.components.get(compId);
        const newComponent: CanvasComponent = {
          id: compId,
          artifactId,
          jsonTree: (jsonTree as ComponentTree) ?? existingComponent?.jsonTree,
          status: 'streaming',
          progress,
          updatedAt: now,
          createdAt: existingComponent?.createdAt ?? now,
        };

        const newComponents = new Map(canvas.components);
        newComponents.set(compId, newComponent);

        updated.set(canvasId, {
          ...canvas,
          components: newComponents,
          status: 'streaming',
          progress,
          updatedAt: now,
        });

        return updated;
      });

      // Auto-show panel and set active canvas/component if first content
      if (autoShowOnContent && action !== 'remove') {
        console.log('[useCanvas] Auto-showing panel for canvas:', canvasId, 'component:', compId);
        setPanelVisible(true);
        setActiveCanvasId((prev) => prev ?? canvasId);
        setActiveComponentIdState((prev) => prev ?? compId);
      }
    },
    [autoShowOnContent]
  );

  const handleComplete = useCallback((data: CanvasCompleteData) => {
    const { canvasId, componentId, artifactId, jsonTree, action = 'upsert' } = data;
    // Use stable component ID: prefer provided componentId, then artifactId, then fallback
    const compId = componentId || artifactId || `component-${canvasId}`;

    setCanvases((prev) => {
      const now = Date.now();
      const updated = new Map(prev);
      let canvas = updated.get(canvasId);

      // Handle remove action complete
      if (action === 'remove') {
        if (canvas && canvas.components.has(compId)) {
          const newComponents = new Map(canvas.components);
          newComponents.delete(compId);

          if (newComponents.size === 0) {
            updated.delete(canvasId);
          } else {
            // Check if all remaining components are complete
            const allComplete = Array.from(newComponents.values()).every(
              (c) => c.status === 'complete' || c.status === 'error'
            );
            updated.set(canvasId, {
              ...canvas,
              components: newComponents,
              status: allComplete ? 'complete' : canvas.status,
              progress: undefined, // Clear progress on completion
              updatedAt: now,
            });
          }
        }
        return updated;
      }

      // Handle upsert complete
      if (!canvas) {
        // Create new canvas with completed component
        canvas = {
          id: canvasId,
          components: new Map(),
          status: 'complete',
          updatedAt: now,
          createdAt: now,
        };
      }

      // Preserve existing jsonTree if new one is undefined
      const existingComponent = canvas.components.get(compId);
      const newComponent: CanvasComponent = {
        id: compId,
        artifactId,
        jsonTree: (jsonTree as ComponentTree) ?? existingComponent?.jsonTree,
        status: 'complete',
        progress: undefined, // Clear progress on completion
        updatedAt: now,
        createdAt: existingComponent?.createdAt ?? now,
      };

      const newComponents = new Map(canvas.components);
      newComponents.set(compId, newComponent);

      // Check if all components are complete
      const allComplete = Array.from(newComponents.values()).every(
        (c) => c.status === 'complete' || c.status === 'error'
      );

      updated.set(canvasId, {
        ...canvas,
        components: newComponents,
        status: allComplete ? 'complete' : 'streaming',
        progress: allComplete ? undefined : canvas.progress, // Clear progress when all complete
        updatedAt: now,
      });

      return updated;
    });
  }, []);

  const handleError = useCallback((data: CanvasErrorData) => {
    const { canvasId, componentId, artifactId, error } = data;
    // Use stable component ID
    const compId = componentId || artifactId || `error-${canvasId}`;

    setCanvases((prev) => {
      const now = Date.now();
      const updated = new Map(prev);
      let canvas = updated.get(canvasId);

      if (!canvas) {
        // Create canvas with error component - canvas itself is not in error state
        const errorComponent: CanvasComponent = {
          id: compId,
          artifactId,
          jsonTree: { type: 'Alert', props: { message: error, severity: 'error' } },
          status: 'error',
          error,
          updatedAt: now,
          createdAt: now,
        };
        canvas = {
          id: canvasId,
          components: new Map([[compId, errorComponent]]),
          status: 'complete', // Canvas is complete, just has an error component
          updatedAt: now,
          createdAt: now,
        };
        updated.set(canvasId, canvas);
        return updated;
      }

      // Update existing component or add error component
      const existingComponent = canvas.components.get(compId);
      const errorComponent: CanvasComponent = {
        id: compId,
        artifactId,
        jsonTree: existingComponent?.jsonTree ?? { type: 'Alert', props: { message: error, severity: 'error' } },
        status: 'error',
        error,
        updatedAt: now,
        createdAt: existingComponent?.createdAt ?? now,
      };

      const newComponents = new Map(canvas.components);
      newComponents.set(compId, errorComponent);

      // Don't set canvas status to 'error' - only the component has an error
      // The canvas can still render other components that are fine
      // Only store error at canvas level if ALL components are in error state
      const allComponentsError = Array.from(newComponents.values()).every(
        (c) => c.status === 'error'
      );

      updated.set(canvasId, {
        ...canvas,
        components: newComponents,
        status: allComponentsError ? 'error' : (canvas.status === 'streaming' ? 'streaming' : 'complete'),
        error: allComponentsError ? error : canvas.error,
        updatedAt: now,
      });

      return updated;
    });
  }, []);

  // ============================================================================
  // Actions
  // ============================================================================

  const closeCanvas = useCallback((canvasId: string) => {
    setCanvases((prev) => {
      const updated = new Map(prev);
      updated.delete(canvasId);
      return updated;
    });

    // Update active canvas if we closed the active one
    setActiveCanvasId((prev) => {
      if (prev === canvasId) {
        const remaining = Array.from(canvases.keys()).filter((id) => id !== canvasId);
        return remaining.length > 0 ? remaining[0] : null;
      }
      return prev;
    });

    // Clear active component if it was in the closed canvas
    setActiveComponentIdState((prev) => {
      const canvas = canvases.get(canvasId);
      if (canvas && prev && canvas.components.has(prev)) {
        return null;
      }
      return prev;
    });
  }, [canvases]);

  const closeComponent = useCallback((canvasId: string, componentId: string) => {
    setCanvases((prev) => {
      const canvas = prev.get(canvasId);
      if (!canvas) return prev;

      const newComponents = new Map(canvas.components);
      newComponents.delete(componentId);

      // If canvas is empty after removal, remove it too
      if (newComponents.size === 0) {
        const updated = new Map(prev);
        updated.delete(canvasId);
        return updated;
      }

      const updated = new Map(prev);
      updated.set(canvasId, {
        ...canvas,
        components: newComponents,
        updatedAt: Date.now(),
      });
      return updated;
    });

    // Update active component if we closed it
    setActiveComponentIdState((prev) => {
      if (prev === componentId) {
        const canvas = canvases.get(canvasId);
        if (canvas) {
          const remaining = Array.from(canvas.components.keys()).filter((id) => id !== componentId);
          return remaining.length > 0 ? remaining[0] : null;
        }
        return null;
      }
      return prev;
    });
  }, [canvases]);

  const setActiveCanvas = useCallback((canvasId: string) => {
    setActiveCanvasId(canvasId);
    // Set first component as active when switching canvases
    const canvas = canvases.get(canvasId);
    if (canvas && canvas.components.size > 0) {
      const firstComponentId = Array.from(canvas.components.keys())[0];
      setActiveComponentIdState(firstComponentId);
    }
  }, [canvases]);

  const setActiveComponent = useCallback((canvasId: string, componentId: string) => {
    setActiveCanvasId(canvasId);
    setActiveComponentIdState(componentId);
  }, []);

  const togglePanel = useCallback(() => {
    setPanelVisible((prev) => !prev);
  }, []);

  const setPanelWidth = useCallback(
    (width: number) => {
      const clampedWidth = Math.max(minPanelWidth, Math.min(maxPanelWidth, width));
      setPanelWidthState(clampedWidth);
    },
    [minPanelWidth, maxPanelWidth]
  );

  const clearAllCanvases = useCallback(() => {
    setCanvases(new Map());
    setActiveCanvasId(null);
    setActiveComponentIdState(null);
  }, []);

  // ============================================================================
  // Connection Handlers (T068a)
  // ============================================================================

  const handleConnectionLost = useCallback(() => {
    setIsConnectionLost(true);
    // Mark any streaming components as interrupted
    setCanvases((prev) => {
      let hasChanges = false;
      const updated = new Map(prev);

      for (const [canvasId, canvas] of updated) {
        let canvasChanged = false;
        const newComponents = new Map(canvas.components);

        for (const [compId, comp] of newComponents) {
          if (comp.status === 'streaming') {
            canvasChanged = true;
            newComponents.set(compId, {
              ...comp,
              status: 'error',
              error: 'Connection lost during streaming',
            });
          }
        }

        if (canvasChanged) {
          hasChanges = true;
          updated.set(canvasId, {
            ...canvas,
            components: newComponents,
            status: 'error',
            error: 'Connection lost during streaming',
          });
        }
      }

      return hasChanges ? updated : prev;
    });
  }, []);

  const handleConnectionRestored = useCallback(() => {
    setIsConnectionLost(false);
  }, []);

  // ============================================================================
  // Derived State
  // ============================================================================

  const activeCanvas = activeCanvasId ? canvases.get(activeCanvasId) ?? null : null;

  // Check if any components are currently streaming
  const hasStreamingCanvases = Array.from(canvases.values()).some((canvas) =>
    Array.from(canvas.components.values()).some((comp) => comp.status === 'streaming')
  );

  return {
    // State
    canvases,
    activeCanvasId,
    activeComponentId,
    activeCanvas,
    panelVisible,
    panelWidth,
    canvasCount,
    componentCount,
    isAtLimit,

    // Connection state (T068a)
    isConnectionLost,
    hasStreamingCanvases,

    // Event Handlers
    handleUpdate,
    handleComplete,
    handleError,
    handleConnectionLost,
    handleConnectionRestored,

    // Actions
    closeCanvas,
    closeComponent,
    setActiveCanvas,
    setActiveComponent,
    togglePanel,
    setPanelWidth,
    clearAllCanvases,
  };
}
